import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Mail, Settings, Send, CheckCircle, XCircle, AlertTriangle,
  Loader2, Eye, EyeOff, RefreshCw, Users, Shield, Save
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

const EmailConfiguration = () => {
  const { getAuthHeaders, isSuperAdmin } = useAuth();
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // SMTP Config state
  const [smtpConfig, setSmtpConfig] = useState({
    host: '',
    port: '587',
    user: '',
    password: '',
    from_email: '',
    from_name: 'Ecowater Workspace',
    use_tls: true
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Diagnostics state
  const [diagnostics, setDiagnostics] = useState(null);
  const [loadingDiagnostics, setLoadingDiagnostics] = useState(false);

  // Test email state
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Badge holders state
  const [badgeHolders, setBadgeHolders] = useState(null);
  const [loadingBadgeHolders, setLoadingBadgeHolders] = useState(false);

  useEffect(() => {
    fetchDiagnostics();
    fetchSmtpConfig();
    fetchBadgeHolders();
  }, []);

  const fetchSmtpConfig = async () => {
    try {
      const response = await fetch(`${API_URL}/api/admin/email-config`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        if (data.config) {
          setSmtpConfig(prev => ({
            ...prev,
            ...data.config,
            password: '' // Don't show password
          }));
        }
      }
    } catch (error) {
      console.error('Error fetching SMTP config:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDiagnostics = async () => {
    setLoadingDiagnostics(true);
    try {
      const response = await fetch(`${API_URL}/api/expenses/admin/email-diagnostics`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setDiagnostics(await response.json());
      }
    } catch (error) {
      console.error('Error fetching diagnostics:', error);
    } finally {
      setLoadingDiagnostics(false);
    }
  };

  const fetchBadgeHolders = async () => {
    setLoadingBadgeHolders(true);
    try {
      const response = await fetch(`${API_URL}/api/expenses/admin/badge-holders-emails`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        setBadgeHolders(await response.json());
      }
    } catch (error) {
      console.error('Error fetching badge holders:', error);
    } finally {
      setLoadingBadgeHolders(false);
    }
  };

  const saveSmtpConfig = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/email-config`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(smtpConfig)
      });

      if (response.ok) {
        toast.success('Email configuration saved successfully');
        fetchDiagnostics();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to save configuration');
      }
    } catch (error) {
      toast.error('Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmail) {
      toast.error('Please enter an email address');
      return;
    }

    setSendingTest(true);
    setTestResult(null);

    try {
      const response = await fetch(`${API_URL}/api/admin/send-test-email`, {
        method: 'POST',
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: testEmail })
      });

      const data = await response.json();

      if (response.ok) {
        setTestResult({ success: true, message: data.message });
        toast.success('Test email sent successfully!');
      } else {
        setTestResult({ success: false, message: data.detail });
        toast.error(data.detail || 'Failed to send test email');
      }
    } catch (error) {
      setTestResult({ success: false, message: 'Network error' });
      toast.error('Failed to send test email');
    } finally {
      setSendingTest(false);
    }
  };

  if (!isSuperAdmin()) {
    return (
      <div className="p-6">
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-6 text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-red-800">Access Denied</h2>
            <p className="text-red-600">Only Super Admins can access email configuration.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Email Configuration</h1>
          <p className="text-gray-500">Configure SMTP settings and test email delivery</p>
        </div>
        <Button variant="outline" onClick={() => { fetchDiagnostics(); fetchBadgeHolders(); }}>
          <RefreshCw className="h-4 w-4 mr-2" />
          Refresh Status
        </Button>
      </div>

      {/* Status Overview */}
      <Card className={diagnostics?.email_configured ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            {loadingDiagnostics ? (
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            ) : diagnostics?.email_configured ? (
              <>
                <CheckCircle className="h-8 w-8 text-green-600" />
                <div>
                  <p className="font-semibold text-green-800">Email is Configured</p>
                  <p className="text-sm text-green-600">
                    Server: {diagnostics.smtp_host}:{diagnostics.smtp_port}
                  </p>
                </div>
              </>
            ) : (
              <>
                <XCircle className="h-8 w-8 text-red-600" />
                <div>
                  <p className="font-semibold text-red-800">Email Not Configured</p>
                  <p className="text-sm text-red-600">
                    Missing: {diagnostics?.missing_config?.join(', ') || 'Unknown'}
                  </p>
                </div>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="config" className="space-y-4">
        <TabsList>
          <TabsTrigger value="config" className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            SMTP Configuration
          </TabsTrigger>
          <TabsTrigger value="test" className="flex items-center gap-2">
            <Send className="h-4 w-4" />
            Test Email
          </TabsTrigger>
          <TabsTrigger value="recipients" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Badge Holders
          </TabsTrigger>
        </TabsList>

        {/* SMTP Configuration Tab */}
        <TabsContent value="config">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Mail className="h-5 w-5" />
                SMTP Server Settings
              </CardTitle>
              <CardDescription>
                Configure your email server settings for sending notifications
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>SMTP Host *</Label>
                  <Input
                    data-testid="smtp-host-input"
                    placeholder="mail.example.com"
                    value={smtpConfig.host}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>SMTP Port *</Label>
                  <Input
                    data-testid="smtp-port-input"
                    placeholder="587"
                    value={smtpConfig.port}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, port: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>SMTP Username *</Label>
                  <Input
                    data-testid="smtp-user-input"
                    placeholder="user@example.com"
                    value={smtpConfig.user}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, user: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>SMTP Password *</Label>
                  <div className="relative">
                    <Input
                      data-testid="smtp-password-input"
                      type={showPassword ? 'text' : 'password'}
                      placeholder="Enter password"
                      value={smtpConfig.password}
                      onChange={(e) => setSmtpConfig({ ...smtpConfig, password: e.target.value })}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="absolute right-2 top-1/2 -translate-y-1/2"
                      onClick={() => setShowPassword(!showPassword)}
                      data-testid="toggle-password-btn"
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500">Leave blank to keep existing password</p>
                </div>
                <div className="space-y-2">
                  <Label>From Email *</Label>
                  <Input
                    data-testid="smtp-from-email-input"
                    placeholder="noreply@example.com"
                    value={smtpConfig.from_email}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, from_email: e.target.value })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>From Name</Label>
                  <Input
                    data-testid="smtp-from-name-input"
                    placeholder="Ecowater Workspace"
                    value={smtpConfig.from_name}
                    onChange={(e) => setSmtpConfig({ ...smtpConfig, from_name: e.target.value })}
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
                <Switch
                  data-testid="smtp-tls-switch"
                  checked={smtpConfig.use_tls}
                  onCheckedChange={(checked) => setSmtpConfig({ ...smtpConfig, use_tls: checked })}
                />
                <div>
                  <Label>Use TLS Encryption</Label>
                  <p className="text-xs text-gray-500">Recommended for secure email delivery</p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button data-testid="save-smtp-config-btn" onClick={saveSmtpConfig} disabled={saving}>
                  {saving ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4 mr-2" />
                      Save Configuration
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Test Email Tab */}
        <TabsContent value="test">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Send className="h-5 w-5" />
                Send Test Email
              </CardTitle>
              <CardDescription>
                Verify your email configuration by sending a test email
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              {!diagnostics?.email_configured && (
                <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3" data-testid="email-not-configured-warning">
                  <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-yellow-800">Email not configured</p>
                    <p className="text-sm text-yellow-600">
                      Please configure SMTP settings first before sending test emails.
                    </p>
                  </div>
                </div>
              )}

              <div className="flex gap-4">
                <div className="flex-1">
                  <Label>Recipient Email Address</Label>
                  <Input
                    data-testid="test-email-input"
                    type="email"
                    placeholder="test@example.com"
                    value={testEmail}
                    onChange={(e) => setTestEmail(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    data-testid="send-test-email-btn"
                    onClick={sendTestEmail}
                    disabled={sendingTest || !diagnostics?.email_configured}
                  >
                    {sendingTest ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Send Test Email
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {testResult && (
                <div className={`p-4 rounded-lg flex items-start gap-3 ${
                  testResult.success 
                    ? 'bg-green-50 border border-green-200' 
                    : 'bg-red-50 border border-red-200'
                }`}>
                  {testResult.success ? (
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5" />
                  ) : (
                    <XCircle className="h-5 w-5 text-red-600 mt-0.5" />
                  )}
                  <div>
                    <p className={`font-medium ${testResult.success ? 'text-green-800' : 'text-red-800'}`}>
                      {testResult.success ? 'Test Email Sent!' : 'Failed to Send'}
                    </p>
                    <p className={`text-sm ${testResult.success ? 'text-green-600' : 'text-red-600'}`}>
                      {testResult.message}
                    </p>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Badge Holders Tab */}
        <TabsContent value="recipients">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Expense Workflow Recipients
              </CardTitle>
              <CardDescription>
                Employees with special badges who will receive workflow notifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              {loadingBadgeHolders ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
                </div>
              ) : badgeHolders ? (
                <div className="space-y-4">
                  {/* Summary */}
                  <div className="grid grid-cols-3 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-700">{badgeHolders.summary?.total || 0}</p>
                      <p className="text-sm text-blue-600">Total Badge Holders</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-700">{badgeHolders.summary?.with_valid_email || 0}</p>
                      <p className="text-sm text-green-600">With Valid Email</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-red-700">{badgeHolders.summary?.without_email || 0}</p>
                      <p className="text-sm text-red-600">Missing Email</p>
                    </div>
                  </div>

                  {badgeHolders.summary?.without_email > 0 && (
                    <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start gap-3">
                      <AlertTriangle className="h-5 w-5 text-yellow-600 mt-0.5" />
                      <div>
                        <p className="font-medium text-yellow-800">Some badge holders are missing email addresses</p>
                        <p className="text-sm text-yellow-600">
                          These employees will NOT receive workflow notifications. Please update their profiles.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Badge Holders List */}
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left font-medium">Employee</th>
                          <th className="px-4 py-3 text-left font-medium">Badges</th>
                          <th className="px-4 py-3 text-left font-medium">Email</th>
                          <th className="px-4 py-3 text-left font-medium">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {badgeHolders.badge_holders?.map((holder, idx) => (
                          <tr key={idx} className="border-t">
                            <td className="px-4 py-3 font-medium">{holder.name}</td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {holder.badges?.map((badge, bidx) => (
                                  <Badge 
                                    key={bidx} 
                                    variant="outline"
                                    className={
                                      badge === 'reviewer' ? 'bg-blue-50 text-blue-700 border-blue-200' :
                                      badge === 'approver' ? 'bg-green-50 text-green-700 border-green-200' :
                                      badge === 'authorizer' ? 'bg-purple-50 text-purple-700 border-purple-200' :
                                      ''
                                    }
                                  >
                                    {badge}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3 text-gray-600">
                              {holder.email || <span className="text-red-500 italic">Not set</span>}
                            </td>
                            <td className="px-4 py-3">
                              {holder.has_valid_email ? (
                                <Badge className="bg-green-100 text-green-700">
                                  <CheckCircle className="h-3 w-3 mr-1" />
                                  Will receive emails
                                </Badge>
                              ) : (
                                <Badge className="bg-red-100 text-red-700">
                                  <XCircle className="h-3 w-3 mr-1" />
                                  No email
                                </Badge>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No badge holders found</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmailConfiguration;
